import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  API_BASE_URL,
  getAuthHeaders,
  getPromptById,
  listAiCatalogModels,
  listProjectToolAllowlist,
  getSessionLockedRecord,
  clearSessionLockedRecord,
  clearEmbeddingWaitRequest,
  getEmbeddingRequest,
  updateProjectSummaryFromSession,
} from '../utils/api';
import { useGroup } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';
import './SessionChatPage.css';
import MessageInputEditor from '../components/MessageInputEditor';
import ChatMessageContent from '../components/ChatMessageContent';
import ProjectWorkflowButtonsCarousel from '../components/ProjectWorkflowButtonsCarousel';
import WebSearchSourcesList from '../components/WebSearchSourcesList';
import YumlEmissionLink from '../components/YumlEmissionLink';
import ConversationIngestModal from '../components/ConversationIngestModal';
import FindRecordModal from '../components/FindRecordModal';
import EmbeddingWaitModal from '../components/EmbeddingWaitModal';
import ToolChainWaitModal from '../components/ToolChainWaitModal';
import EntryUpdateWaitModal from '../components/EntryUpdateWaitModal';
import '../components/ConversationIngestModal.css';
import { extractMessageContent } from '../utils/messageContent';
import { copyTextToClipboard } from '../utils/clipboard';
import { collectRequestReplyText, formatThinkingHistoryAudit } from '../utils/chatCopyFormat';
import { UrlTagProvider } from '../utils/tags';
import { canModelAcceptFile, inferAttachmentType, normalizeModelCapabilities } from '../utils/modelCapabilities';
import {
  CHAT_PENDING_STATUSES,
  isActiveChatRequest,
  isBlockingChatRequest,
  shouldContinueRequestPolling,
} from '../utils/chatRequestPolling';
import DurationTimer from '../components/DurationTimer';

// Utility: format duration between two ISO timestamps as M:SS
function formatDuration(startISO, endISO) {
  if (!startISO) return '0:00';
  const start = new Date(startISO).getTime();
  const end = endISO ? new Date(endISO).getTime() : Date.now();
  const sec = Math.max(0, Math.floor((end - start) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Utility: format a byte size into a human-readable string
function formatBytes(bytes) {
  const n = typeof bytes === 'number' ? bytes : Number(bytes || 0);
  if (!isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const val = n / Math.pow(1024, i);
  const fixed = i === 0 ? 0 : val < 10 ? 1 : 0;
  return `${val.toFixed(fixed)} ${units[i]}`;
}

const PAGE_SIZE = 30; // within [1,50]

function formatEmbeddingStatusMeta(metadata) {
  const status = metadata?.embedding_status;
  if (!status || typeof status !== 'object') return null;
  const dateText = status.last_embedded_at
    ? new Date(status.last_embedded_at).toLocaleString()
    : 'No embedding found';
  const field = status.search_field || 'unknown field';
  const group = status.group_slug || 'all groups';
  const matches = status.total_matching ?? 'unknown';
  const coverage =
    status.entry_count != null && status.embedding_count != null
      ? `${status.embedding_count}/${status.entry_count} indexed`
      : `${matches} search matches`;
  const need = status.needs_embedding
    ? ` • needs embedding${status.reason ? ` (${status.reason})` : ''}`
    : '';
  return {
    text: `Embedding: ${field} (${group}) • ${coverage} • ${dateText}${need}`,
    warning: status.warning || null,
  };
}

// Utility: merge new messages into an existing array by created_at while avoiding duplicates by message_id.
// Returns a new array sorted oldest -> newest.
function mergeMessagesByTimeUnique(existing, incoming) {
  const byId = new Map();
  (existing || []).forEach(m => { if (m && m.message_id) byId.set(m.message_id, m); });
  (incoming || []).forEach(m => { if (m && m.message_id && !byId.has(m.message_id)) byId.set(m.message_id, m); });
  const result = Array.from(byId.values());
  result.sort((a, b) => {
    const ta = new Date(a.created_at).getTime();
    const tb = new Date(b.created_at).getTime();
    if (ta !== tb) return ta - tb;
    // Tie-breaker: place hidden/system messages (metadata.visible === false) AFTER visible ones when timestamps equal
    const ah = a?.metadata && a.metadata.visible === false ? 1 : 0;
    const bh = b?.metadata && b.metadata.visible === false ? 1 : 0;
    if (ah !== bh) return ah - bh;
    // Final tie-breaker: stable by message_id
    const ai = a.message_id || '';
    const bi = b.message_id || '';
    if (ai < bi) return -1;
    if (ai > bi) return 1;
    return 0;
  });
  return result;
}

// Fetch multiple messages by IDs (API returns oldest -> newest)
async function fetchMessagesByIds(apiBase, messageIds, getAuthHeadersFn) {
  if (!Array.isArray(messageIds) || messageIds.length === 0) return [];
  const headers = await getAuthHeadersFn();
  const resp = await fetch(`${apiBase}/casc/v1/api/chat/message/get-multiple`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ messageIds })
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to load messages');
  const data = Array.isArray(json.data) ? json.data : [];
  return data;
}

const SessionChatPage = () => {
  const { groups, activeSuffix } = useGroup();
  const { user, chatMarkdownEnabled } = useAuth();
  const isSuperAdmin = user?.accountType === 'superadmin';
  const [verboseLogs, setVerboseLogs] = useState(false);
  const { projectId, sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [errorSession, setErrorSession] = useState('');

  const [messages, setMessages] = useState([]);
  const [, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [errorMsgs, setErrorMsgs] = useState('');
  // Older history loading helpers
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [reachedStart, setReachedStart] = useState(false);

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [archivingChat, setArchivingChat] = useState(false);
  const [updatingSummary, setUpdatingSummary] = useState(false);
  const [ingestMessage, setIngestMessage] = useState(null);
  const [findRecordOpen, setFindRecordOpen] = useState(false);
  const [findRecordGroupSlug, setFindRecordGroupSlug] = useState(null);
  const [embeddingWaitOpen, setEmbeddingWaitOpen] = useState(false);
  const [embeddingWaitRequest, setEmbeddingWaitRequest] = useState(null);
  const [toolChainWaitOpen, setToolChainWaitOpen] = useState(false);
  const [toolChainWaitRequest, setToolChainWaitRequest] = useState(null);
  const [entryUpdateWaitOpen, setEntryUpdateWaitOpen] = useState(false);
  const [entryUpdateWaitRequest, setEntryUpdateWaitRequest] = useState(null);
  const queuedEmbedPollRef = useRef(null);
  const [lockedRecord, setLockedRecord] = useState(null);
  const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [replyCopyNotice, setReplyCopyNotice] = useState({ messageId: null, text: '' });
  const replyCopyNoticeTimerRef = useRef(null);

  // attachments state (selected files ready to send)
  const [attachments, setAttachments] = useState([]); // { name, mime, size, data }
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [chatModelMeta, setChatModelMeta] = useState(null);
  const [projectToolIds, setProjectToolIds] = useState(() => new Set());

  const projectGroupSlugs = useMemo(() => (
    new Set((groups || []).map((g) => g.group_slug || g.id).filter(Boolean))
  ), [groups]);

  const chatModelCapabilities = useMemo(() => normalizeModelCapabilities(chatModelMeta || {}), [chatModelMeta]);
  const canAttachAnyFile = !chatModelMeta || (
    chatModelCapabilities.acceptsText
    || chatModelCapabilities.acceptsImages
    || chatModelCapabilities.acceptsPdf
    || chatModelCapabilities.acceptsVideo
    || chatModelCapabilities.acceptsAudio
  );

  // Helpers to add/remove files
  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('Failed to read file'));
    fr.readAsDataURL(file);
  });

  const addFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const prepared = [];
    const rejected = [];
    for (const f of files) {
      try {
        if (chatModelMeta && !canModelAcceptFile(chatModelMeta, f)) {
          rejected.push({ name: f.name, type: inferAttachmentType(f) });
          continue;
        }
        const dataUrl = await readFileAsDataUrl(f);
        prepared.push({ name: f.name, mime: f.type || 'application/octet-stream', size: f.size, data: dataUrl });
      } catch (e) {
        // ignore individual failures
      }
    }
    if (prepared.length > 0) setAttachments(prev => [...prev, ...prepared]);
    if (rejected.length > 0) {
      const summary = rejected.map((r) => `${r.name} (${r.type})`).slice(0, 4).join(', ');
      setSendError(`Skipped unsupported attachments for this model: ${summary}${rejected.length > 4 ? '...' : ''}`);
    }
  };

  const removeAttachmentAt = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  // request tracking
  const [activeRequests, setActiveRequests] = useState([]);
  const [expandedReqs, setExpandedReqs] = useState({});

  const listRef = useRef(null);
  const bottomRef = useRef(null);
  const stickyRef = useRef(true); // whether we should auto-stick to bottom on new messages
  const lastPollRef = useRef(0); // enforce max 1 request/second
  const pollingInFlightRef = useRef(false); // ensure no overlapping polls
  const pollTimeoutRef = useRef(null); // handle chained timeout scheduling
  const pollLoopRunningRef = useRef(false); // avoid starting multiple loops
  const initialScrollDoneRef = useRef(false); // ensure we only autoscroll once per session
  const initialJumpPendingRef = useRef(true); // wait until initial messages render to jump to bottom

  // Keep refs in sync for stable callbacks without re-creating them frequently
  const messagesRef = useRef(messages);
  const activeRequestsRef = useRef(activeRequests);
  const expandedReqsRef = useRef(expandedReqs);
  const loadMessagesRef = useRef(null);
  const ensureRequestHistoryRef = useRef(null);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { activeRequestsRef.current = activeRequests; }, [activeRequests]);
  useEffect(() => { expandedReqsRef.current = expandedReqs; }, [expandedReqs]);

  // Sticky threshold: consider the view "near bottom" when within half the visible height

  // Helper: load older messages using window API around the first currently loaded message
  const loadOlderWindow = React.useCallback(async () => {
    if (!sessionId) return false;
    if (loadingMsgs || loadingOlder) return false;
    const current = messagesRef.current || [];
    if (current.length === 0) return false;
    const first = current[0];
    if (!first || !first.message_id) return false;
    setLoadingOlder(true);
    try {
      const before = Math.min(PAGE_SIZE, 49); // ensure before+after+1 <= 50
      const payload = { sessionId, startMessageId: first.message_id, before, after: 0 };
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/message/at`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to load older messages');
      const windowItems = Array.isArray(json.data) ? json.data : [];
      // API guarantees oldest -> newest in the window
      const el = listRef.current;
      const prevHeight = el ? el.scrollHeight : 0;
      const prevIds = new Set(current.map(m => m.message_id));
      const newOnes = windowItems.filter(m => m && m.message_id && !prevIds.has(m.message_id));
      if (newOnes.length === 0) {
        // If API returned only the start message or nothing, we've reached the beginning
        setReachedStart(true);
        return false;
      }
      setMessages(prev => mergeMessagesByTimeUnique(prev, newOnes));
      // Preserve viewport position relative to the items user is viewing
      requestAnimationFrame(() => {
        if (!el) return;
        const newHeight = el.scrollHeight;
        el.scrollTop = Math.max(0, newHeight - prevHeight);
      });
      // Update total best-effort by added unique count
      setTotal(prevTotal => prevTotal + newOnes.length);
      return true;
    } catch (_) {
      return false;
    } finally {
      setLoadingOlder(false);
    }
  }, [sessionId, loadingMsgs, loadingOlder]);

  // Guard missing params
  useEffect(() => {
    if (!projectId) {
      navigate('/projects', { replace: true });
    }
  }, [projectId, navigate]);

  // totalPages no longer used; paging of /message/list is not used beyond page 1

  // Load session info (with owner resolution)
  const loadSession = React.useCallback(async () => {
    if (!sessionId) return;
    setLoadingSession(true);
    setErrorSession('');
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/get`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ sessionId }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to load session');
      const sessionData = json.data;
      // If session has created_by but no owner, try to resolve from the session list
      if (sessionData?.created_by && !sessionData?.owner && projectId) {
        try {
          const listResp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/list`, {
            method: 'POST', headers,
            body: JSON.stringify({ projectId, page: 1, limit: 100 }),
          });
          const listJson = await listResp.json().catch(() => ({}));
          if (listResp.ok && listJson?.success) {
            const match = (listJson.data?.items || []).find(s => s.session_id === sessionId);
            if (match?.owner) sessionData.owner = match.owner;
          }
        } catch (_) { /* ignore */ }
      }
      setSession(sessionData);
    } catch (e) {
      setErrorSession(e.message || 'Failed to load session');
    } finally {
      setLoadingSession(false);
    }
  }, [sessionId, projectId]);

  const refreshLockedRecord = React.useCallback(async ({
    openFindIfRequested = false,
    openEmbeddingWaitIfRequested = false,
    openToolChainWaitIfRequested = false,
    openEntryUpdateWaitIfRequested = false,
  } = {}) => {
    if (!sessionId) {
      setLockedRecord(null);
      setEmbeddingWaitRequest(null);
      setToolChainWaitRequest(null);
      setEntryUpdateWaitRequest(null);
      return;
    }
    try {
      const data = await getSessionLockedRecord(sessionId);
      setLockedRecord(data.locked_record || null);
      if (openFindIfRequested && data.find_record_request) {
        setFindRecordGroupSlug(data.find_record_request.group_slug || null);
        setFindRecordOpen(true);
      }
      const waitReq = data.embedding_wait_request || null;
      setEmbeddingWaitRequest(waitReq);
      if (openEmbeddingWaitIfRequested && waitReq?.tool_slug && !waitReq.mode) {
        setEmbeddingWaitOpen(true);
      }
      const chainWait = data.tool_chain_wait_request || null;
      setToolChainWaitRequest(chainWait);
      if (openToolChainWaitIfRequested && chainWait?.chain_tool_slug) {
        setToolChainWaitOpen(true);
      }
      const entryWait = data.entry_update_wait_request || null;
      setEntryUpdateWaitRequest(entryWait);
      if (openEntryUpdateWaitIfRequested && entryWait?.tool_slug && entryWait?.entry_id) {
        setEntryUpdateWaitOpen(true);
      }
    } catch (_) {
      // Non-fatal: older horizons may lack session metadata
    }
  }, [sessionId]);

  useEffect(() => {
    refreshLockedRecord({
      openFindIfRequested: true,
      openEmbeddingWaitIfRequested: true,
      openToolChainWaitIfRequested: true,
      openEntryUpdateWaitIfRequested: true,
    });
  }, [refreshLockedRecord]);

  useEffect(() => () => {
    if (queuedEmbedPollRef.current) {
      clearInterval(queuedEmbedPollRef.current);
      queuedEmbedPollRef.current = null;
    }
  }, []);

  // Resume background poll if a queued deferred message was left mid-flight
  useEffect(() => {
    if (!sessionId || !embeddingWaitRequest?.request_id) return undefined;
    if (String(embeddingWaitRequest.mode || '').toLowerCase() !== 'queue') return undefined;
    const deferred = String(embeddingWaitRequest.deferred_message || '').trim();
    if (!deferred) return undefined;
    if (queuedEmbedPollRef.current) return undefined;

    const requestId = embeddingWaitRequest.request_id;
    let tries = 0;
    queuedEmbedPollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const row = await getEmbeddingRequest(requestId);
        const status = String(row?.status || row?.request?.status || '').toLowerCase();
        if (status === 'finished') {
          clearInterval(queuedEmbedPollRef.current);
          queuedEmbedPollRef.current = null;
          try { await clearEmbeddingWaitRequest(sessionId); } catch (_) { /* ignore */ }
          setEmbeddingWaitRequest(null);
          handleSend(deferred);
        } else if (status === 'errored' || status === 'canceled' || status === 'cancelled' || tries > 180) {
          clearInterval(queuedEmbedPollRef.current);
          queuedEmbedPollRef.current = null;
          setSendError(`Queued embedding ended with status: ${status || 'timeout'}`);
        }
      } catch (_) {
        if (tries > 180) {
          clearInterval(queuedEmbedPollRef.current);
          queuedEmbedPollRef.current = null;
        }
      }
    }, 2000);

    return () => {
      if (queuedEmbedPollRef.current) {
        clearInterval(queuedEmbedPollRef.current);
        queuedEmbedPollRef.current = null;
      }
    };
    // intentionally omit handleSend — resume once per wait-request identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, embeddingWaitRequest?.request_id, embeddingWaitRequest?.mode, embeddingWaitRequest?.deferred_message]);

  const lastUserMessageText = React.useMemo(() => {
    const list = Array.isArray(messages) ? messages : [];
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const m = list[i];
      if (m?.metadata?.visible === false) continue;
      if (m?.is_agent) continue;
      const content = extractMessageContent(m?.content);
      if (content && String(content).trim()) return String(content).trim();
    }
    return '';
  }, [messages]);

  const handleUnlockRecord = async () => {
    if (!sessionId) return;
    setLockBusy(true);
    try {
      await clearSessionLockedRecord(sessionId);
      setLockedRecord(null);
      setUnlockConfirmOpen(false);
    } catch (err) {
      setSendError(err.message || 'Failed to unlock record');
    } finally {
      setLockBusy(false);
    }
  };

  const loadChatModelCapabilities = React.useCallback(async () => {
    if (!projectId) {
      setChatModelMeta(null);
      return;
    }

    try {
      const promptResult = await getPromptById(projectId);
      const prompt = promptResult?.prompt || null;
      const modelId = String(prompt?.model || '').trim();
      if (!modelId) {
        setChatModelMeta(null);
        return;
      }

      const providerHint = String(prompt?.runtime?.provider?.slug || '').trim().toLowerCase();
      const catalogResult = await listAiCatalogModels({ q: modelId, limit: 100 });
      const candidates = Array.isArray(catalogResult?.models) ? catalogResult.models : [];
      const exactByProvider = providerHint
        ? candidates.find((m) => String(m?.id || '').trim() === modelId && String(m?.providerId || '').trim().toLowerCase() === providerHint)
        : null;
      const exactAny = candidates.find((m) => String(m?.id || '').trim() === modelId) || null;
      const match = exactByProvider || exactAny;

      setChatModelMeta(match ? {
        id: match.id,
        name: match.name || match.id,
        capabilities: match.capabilities,
        modalities: match.modalities,
        modelType: match.modelType,
        isEmbedding: match.isEmbedding,
      } : {
        id: modelId,
        name: modelId,
        capabilities: { acceptsText: true, canToolCall: true },
      });
    } catch (_) {
      setChatModelMeta(null);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setProjectToolIds(new Set());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await listProjectToolAllowlist();
        if (cancelled) return;
        const ids = new Set(
          (Array.isArray(res?.ids) ? res.ids : [])
            .map((id) => String(id || '').trim().toLowerCase())
            .filter(Boolean)
        );
        setProjectToolIds(ids);
      } catch (_) {
        if (!cancelled) setProjectToolIds(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Active request polling (targeted). Returns boolean indicating whether another check is needed.
  const refreshActiveRequests = React.useCallback(async (specificIds) => {
    if (!sessionId) return false;
    // Do not start a new check while a previous one is in flight
    if (pollingInFlightRef.current) return true; // try again later
    // Enforce a 1s gap since the last COMPLETED check
    const now = Date.now();
    if (now - (lastPollRef.current || 0) < 1000) return true; // too soon; keep loop alive
    // Helper to collect request IDs referenced by current hidden messages from ref
    const collectIdsFromMessagesRef = () => {
      const ids = new Set();
      (messagesRef.current || []).forEach(m => {
        if (m?.metadata && m.metadata.visible === false) {
          try {
            const parsed = JSON.parse(m.content);
            if (parsed?.request_id) ids.add(parsed.request_id);
          } catch (_) { /* ignore */ }
        }
      });
      return Array.from(ids);
    };
    const requestIds = Array.isArray(specificIds) ? specificIds : collectIdsFromMessagesRef();
    if (!requestIds || requestIds.length === 0) return false; // nothing to check
    let shouldContinue = true; // default to continue unless we can prove all are terminal
    try {
      pollingInFlightRef.current = true;
      // Use the request/get API to fetch specific requests by IDs (batched)
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/message/request/get`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ requestIds }),
      });
      const json = await resp.json().catch(() => ({}));
      if (resp.ok && json?.success) {
        // Merge/update only the returned requests into activeRequests map
        const updates = Array.isArray(json.data)
          ? json.data
          : (Array.isArray(json?.data?.items) ? json.data.items : []);
        // Capture previous statuses to detect transitions
        const prevMap = new Map((activeRequestsRef.current || []).map(r => [r.request_id, r]));
        setActiveRequests(prev => {
          const map = new Map((prev || []).map(r => [r.request_id, r]));
          updates.forEach(u => {
            if (!u?.request_id) return;
            const prevReq = map.get(u.request_id) || {};
            // Merge fields, preserving previously known data
            const merged = { ...prevReq, ...u };
            // Compute modified_at from history when available (fallback to existing)
            if (merged.history_updated_at) {
              try {
                const h = new Date(merged.history_updated_at).getTime();
                const m = merged.modified_at ? new Date(merged.modified_at).getTime() : 0;
                if (!Number.isNaN(h) && (Number.isNaN(m) || h > m)) {
                  merged.modified_at = merged.history_updated_at;
                }
              } catch (_) { /* noop */ }
            }
            map.set(u.request_id, merged);
          });
          return Array.from(map.values());
        });

        // After state update, if any previously pending request is now fulfilled, load its messages
        try {
          const toLoadIds = [];
          let needsMessageListReload = false;
          const historyIdsToRefresh = [];
          updates.forEach(u => {
            if (!u?.request_id) return;
            const prev = prevMap.get(u.request_id);
            const prevStatus = (prev?.status || '').toLowerCase();
            const currStatus = (u.status || '').toLowerCase();
            const wasPending = !prev || isActiveChatRequest(prev) || CHAT_PENDING_STATUSES.has(prevStatus);
            if (currStatus === 'fulfilled' && wasPending) {
              const idsSet = new Set(collectIdsFromMessagesRef());
              if (idsSet.has(u.request_id)) {
                if (Array.isArray(u.response_messages) && u.response_messages.length > 0) {
                  toLoadIds.push(...u.response_messages);
                } else {
                  needsMessageListReload = true;
                }
              }
            }
            // Keep thinking history / timer footer fresh while the request is still running
            const prevHistCount = Number(prev?.history_count || 0);
            const currHistCount = Number(u.history_count || 0);
            const histTsChanged =
              Boolean(u.history_updated_at) &&
              u.history_updated_at !== (prev?.history_updated_at || null);
            if (isActiveChatRequest(u) && (currHistCount > prevHistCount || histTsChanged)) {
              const rec = expandedReqsRef.current?.[u.request_id];
              if (rec && (rec.open || Array.isArray(rec.items))) {
                historyIdsToRefresh.push(u.request_id);
              }
            }
          });
          if (historyIdsToRefresh.length > 0 && typeof ensureRequestHistoryRef.current === 'function') {
            historyIdsToRefresh.forEach((id) => {
              try { ensureRequestHistoryRef.current(id, { force: true }); } catch (_) { /* ignore */ }
            });
          }
          if (toLoadIds.length > 0) {
            const newMsgs = await fetchMessagesByIds(API_BASE_URL, toLoadIds, getAuthHeaders);
            if (newMsgs.length > 0) {
              setMessages(prev => mergeMessagesByTimeUnique(prev, newMsgs));
              // Best-effort: bump total by the count of actually new messages inserted
              setTotal(prevTotal => {
                const existingIds = new Set((messagesRef.current || []).map(m => m.message_id));
                const added = newMsgs.filter(m => m && m.message_id && !existingIds.has(m.message_id)).length;
                return prevTotal + added;
              });
            }
            try { await refreshLockedRecord({ openFindIfRequested: true, openEmbeddingWaitIfRequested: true, openToolChainWaitIfRequested: true, openEntryUpdateWaitIfRequested: true }); } catch (_) { /* ignore */ }
          } else if (needsMessageListReload && typeof loadMessagesRef.current === 'function') {
            await loadMessagesRef.current(1);
            try { await refreshLockedRecord({ openFindIfRequested: true, openEmbeddingWaitIfRequested: true, openToolChainWaitIfRequested: true, openEntryUpdateWaitIfRequested: true }); } catch (_) { /* ignore */ }
          }
        } catch (_) { /* swallow fetch/merge errors to avoid breaking poll loop */ }

        // Determine if any of the checked requests are still pending
        if (updates.length > 0) {
          shouldContinue = shouldContinueRequestPolling(updates, requestIds);
        } else if (requestIds.length > 0) {
          // Request rows may not be visible yet; keep polling until we get data or terminal state.
          shouldContinue = true;
        }
      } else {
        // Request failed; keep loop alive to retry later
        shouldContinue = true;
      }
    } catch (_) {
      // On error, keep loop alive to retry later
      shouldContinue = true;
    } finally {
      pollingInFlightRef.current = false;
      // record completion time to ensure next check is scheduled after a full second gap
      lastPollRef.current = Date.now();
    }
    return shouldContinue;
  }, [sessionId, refreshLockedRecord]);

  // Load messages — always fetch only the first page to seed the window
  const loadMessages = React.useCallback(async (pageNum = 1, { prepend = false } = {}) => {
    if (!sessionId) return;
    setLoadingMsgs(true);
    setErrorMsgs('');
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/message/list`, {
        method: 'POST',
        headers,
        // Always request only the first page; older history is fetched via /message/at
        body: JSON.stringify({ sessionId, page: 1, limit: PAGE_SIZE }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to load messages');
      const items = json.data.items || [];
      // collect request ids from hidden messages
      const newReqIds = [];
      items.forEach(m => {
        if (m?.metadata && m.metadata.visible === false) {
          try {
            const parsed = JSON.parse(m.content);
            if (parsed && parsed.request_id) newReqIds.push(parsed.request_id);
          } catch (_) {}
        }
      });
      if (prepend) {
        // Merge with existing while keeping strict chronological order
        setMessages(prev => mergeMessagesByTimeUnique(prev, items));
      } else {
        // Ensure strict chronological order and deterministic tie-breaking
        const sorted = [...items].sort((a, b) => {
          const ta = new Date(a.created_at).getTime();
          const tb = new Date(b.created_at).getTime();
          if (ta !== tb) return ta - tb;
          const ah = a?.metadata && a.metadata.visible === false ? 1 : 0;
          const bh = b?.metadata && b.metadata.visible === false ? 1 : 0;
          if (ah !== bh) return ah - bh;
          const ai = a.message_id || '';
          const bi = b.message_id || '';
          if (ai < bi) return -1;
          if (ai > bi) return 1;
          return 0;
        });
        setMessages(sorted);
      }
      setTotal(json.data.total || 0);
      if (newReqIds.length > 0) {
        await refreshActiveRequests(newReqIds);
      }
      return items;
    } catch (e) {
      setErrorMsgs(e.message || 'Failed to load messages');
      if (!prepend) setMessages([]);
      setTotal(0);
      return [];
    } finally {
      setLoadingMsgs(false);
    }
  }, [sessionId, refreshActiveRequests]);

  useEffect(() => {
    loadMessagesRef.current = loadMessages;
  }, [loadMessages]);

  useEffect(() => { setPage(1); }, [sessionId]);
  useEffect(() => { loadSession(); }, [loadSession]);

  useEffect(() => { loadChatModelCapabilities(); }, [loadChatModelCapabilities]);
  // Initial seed: only fetch first page once per session; no paging
  useEffect(() => { loadMessages(1); }, [loadMessages, sessionId]);

  // When switching sessions, drop any previously tracked requests/history and reset polling timestamp
  useEffect(() => {
    setActiveRequests([]);
    setExpandedReqs({});
    if (lastPollRef) lastPollRef.current = 0;
    // stop any pending poll timers and flags when session changes
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    pollingInFlightRef.current = false;
    pollLoopRunningRef.current = false;
    // Reset initial scroll flags for the new session
    initialScrollDoneRef.current = false;
    initialJumpPendingRef.current = true;
    // Default to sticky on new session so we follow the latest messages
    stickyRef.current = true;
    // Reset history flags
    setReachedStart(false);
    setLoadingOlder(false);
  }, [sessionId]);

  useEffect(() => {
    // Auto-scroll to bottom only once per session, after initial messages render
    if (!initialScrollDoneRef.current && initialJumpPendingRef.current && listRef.current && messages.length > 0) {
      // Defer until after paint so scrollHeight reflects rendered content
      const id = requestAnimationFrame(() => {
        if (bottomRef.current && typeof bottomRef.current.scrollIntoView === 'function') {
          try { bottomRef.current.scrollIntoView({ block: 'end' }); } catch (_) { /* fallback below */ }
        }
        // Fallback to manual scroll as well
        listRef.current.scrollTop = listRef.current.scrollHeight;
        initialScrollDoneRef.current = true;
        initialJumpPendingRef.current = false;
        // We just jumped to bottom; enable sticky so we follow incoming messages
        stickyRef.current = true;
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [messages]);

  // (duplicate removed)

  // Paging jump to last page removed — we only ever fetch page 1 to seed the view

  // Helper: collect all request IDs referenced by current (hidden) messages
  const collectMessageRequestIds = React.useCallback(() => {
    const ids = new Set();
    (messages || []).forEach(m => {
      if (m?.metadata && m.metadata.visible === false) {
        try {
          const parsed = JSON.parse(m.content);
          if (parsed?.request_id) ids.add(parsed.request_id);
        } catch (_) { /* ignore parse errors */ }
      }
    });
    return Array.from(ids);
  }, [messages]);

  // Determine which request IDs are still pending (queued or processing/pending)
  const getPendingRequestIds = React.useCallback(() => {
    const ids = collectMessageRequestIds();
    if (ids.length === 0) return [];
    const byId = new Map((activeRequests || []).map(r => [r.request_id, r]));
    return ids.filter(id => {
      const r = byId.get(id);
      if (!r) return true;
      return isActiveChatRequest(r);
    });
  }, [activeRequests, collectMessageRequestIds]);

  // Active request polling (targeted). Returns boolean indicating whether another check is needed.
  // (moved earlier in the file; duplicate removed)

  // Targeted polling loop that runs only when there are pending request IDs to check.
  // Ensures: (1) No overlapping requests; (2) At least 1s gap between checks after the previous one returns.
  useEffect(() => {
    if (!sessionId) return () => {};

    const startLoopIfNeeded = () => {
      if (pollLoopRunningRef.current) return; // already running
      const initialPending = getPendingRequestIds();
      if (initialPending.length === 0) return; // nothing to track

      pollLoopRunningRef.current = true;

      const tick = async () => {
        // If session changed or loop was stopped, exit early
        if (!sessionId) {
          pollLoopRunningRef.current = false;
          return;
        }
        const pendingIds = getPendingRequestIds();
        if (pendingIds.length === 0) {
          // No pending items; stop the loop
          pollLoopRunningRef.current = false;
          if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
          }
          return;
        }
        const shouldContinue = await refreshActiveRequests(pendingIds);
        // If all checked requests are terminal (errored/canceled/finished), stop without scheduling
        if (!shouldContinue) {
          pollLoopRunningRef.current = false;
          if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
          }
          return;
        }
        // Otherwise schedule next run at least 1s later to enforce gap after completion
        pollTimeoutRef.current = setTimeout(tick, 1000);
      };

      // Kick off the first tick immediately
      tick();
    };

    startLoopIfNeeded();

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      pollLoopRunningRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, messages, activeRequests]);

  // Only treat requests that are actually referenced by the current (hidden) messages
  // as eligible to block the Send button. Otherwise, stale entries in activeRequests
  // from earlier pages could keep the button disabled indefinitely.
  const anyPending = useMemo(() => {
    const ids = new Set(collectMessageRequestIds());
    if (ids.size === 0) return false;
    const now = Date.now();
    return (activeRequests || []).some(r => ids.has(r.request_id) && isBlockingChatRequest(r, now));
  }, [activeRequests, collectMessageRequestIds]);

  const ensureRequestHistory = async (requestId, { force = false } = {}) => {
    const curr = expandedReqsRef.current?.[requestId];
    if (force || !curr || (!curr.items && !curr.loading)) {
      setExpandedReqs(prev => ({ ...prev, [requestId]: { ...(prev[requestId] || {}), loading: true, error: null } }));
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/message/request/history`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ requestId, page: 1, limit: 100 }),
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to load history');
        const items = json.data?.items || [];
        setExpandedReqs(prev => ({ ...prev, [requestId]: { ...(prev[requestId] || {}), loading: false, items, error: null } }));
      } catch (e) {
        setExpandedReqs(prev => ({ ...prev, [requestId]: { ...(prev[requestId] || {}), loading: false, error: e.message || 'Failed to load history' } }));
      }
    }
  };
  ensureRequestHistoryRef.current = ensureRequestHistory;

  // When the tab becomes visible again, resume pending-request polling and soft-refresh history/messages.
  // Browsers throttle timers in background tabs, so this recovers a stale chat without a manual refresh.
  useEffect(() => {
    if (!sessionId) return undefined;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const pendingIds = getPendingRequestIds();
      if (pendingIds.length === 0) return;
      Promise.resolve(refreshActiveRequests(pendingIds)).catch(() => {});
      pendingIds.forEach((id) => {
        const rec = expandedReqsRef.current?.[id];
        if (rec && (rec.open || Array.isArray(rec.items)) && typeof ensureRequestHistoryRef.current === 'function') {
          try { ensureRequestHistoryRef.current(id, { force: true }); } catch (_) { /* ignore */ }
        }
      });
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, refreshActiveRequests]);

  const toggleRequestHistory = async (requestId) => {
    setExpandedReqs(prev => {
      const curr = prev[requestId];
      const nextOpen = !(curr && curr.open);
      return { ...prev, [requestId]: { ...(curr || {}), open: nextOpen } };
    });
    // Prefetch if needed
    ensureRequestHistory(requestId);
  };

  // (formatDuration moved to module scope so it can be used by RequestHistoryBlock)

  // Detect changes to history_updated_at and refresh history lists if we already loaded them
  useEffect(() => {
    if (!activeRequests || activeRequests.length === 0) return;
    // Force reload history for any request whose history_updated_at changed and we already have items
    const toReload = [];
    activeRequests.forEach(r => {
      if (!r?.request_id) return;
      const currTs = r.history_updated_at || null;
      const rec = expandedReqs[r.request_id];
      const lastTs = rec?.last_history_updated_at || null;
      if (currTs && currTs !== lastTs && rec && (rec.open || Array.isArray(rec.items))) {
        toReload.push(r.request_id);
      }
    });
    if (toReload.length > 0) {
      toReload.forEach(id => ensureRequestHistory(id, { force: true }));
    }
    // Persist the latest seen history_updated_at per request so we detect future changes
    setExpandedReqs(prev => {
      let changed = false;
      const next = { ...prev };
      activeRequests.forEach(r => {
        if (!r?.request_id) return;
        const currTs = r.history_updated_at || null;
        const rec = next[r.request_id] || {};
        if (rec.last_history_updated_at !== currTs) {
          next[r.request_id] = { ...rec, last_history_updated_at: currTs };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRequests]);

  const handleSend = async (maybeContent, sendOptions = {}) => {
    const content = (maybeContent ?? text).trim();
    if (!content) return;
    const workflowPromptId = sendOptions?.workflowPromptId && String(sendOptions.workflowPromptId).trim()
      ? String(sendOptions.workflowPromptId).trim()
      : null;
    const outgoingFiles = (attachments || []).filter((f) => !chatModelMeta || canModelAcceptFile(chatModelMeta, f));
    if ((attachments || []).length !== outgoingFiles.length) {
      const dropped = (attachments || []).filter((f) => !outgoingFiles.includes(f));
      const names = dropped.map((f) => `${f.name} (${inferAttachmentType(f)})`).slice(0, 4).join(', ');
      setSendError(`Some attachments were removed because the current model does not support them: ${names}${dropped.length > 4 ? '...' : ''}`);
    }
    setSending(true);
    if ((attachments || []).length === outgoingFiles.length) {
      setSendError('');
    }
    try {
      if (!sessionId) {
        if (!projectId) throw new Error('Missing project');
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/create`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            project_id: projectId,
            name: null,
            message: {
              content,
              files: outgoingFiles,
              is_agent: false,
              ...(workflowPromptId ? { workflowPromptId } : {}),
              ...(verboseLogs ? { verbose: true } : {}),
            },
          }),
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to create session');
        const createdSession = json.data?.session || json.data;
        const sid = createdSession?.session_id || json.data?.session_id;
        // Notify nav menu about the new session so it appears immediately
        try {
          const detail = {
            session_id: createdSession?.session_id || sid,
            name: createdSession?.name || null,
            created_at: createdSession?.created_at || new Date().toISOString(),
            modified_at: createdSession?.modified_at || createdSession?.created_at || new Date().toISOString(),
            project_id: projectId,
          };
          window.dispatchEvent(new CustomEvent('cascade:session-created', { detail }));
        } catch (_) { /* no-op */ }
        setText('');
        setAttachments([]);
        navigate(`/p/${projectId}/c/${sid}`);
      } else {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/message/send`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            sessionId,
            content,
            files: outgoingFiles,
            is_agent: false,
            ...(workflowPromptId ? { workflowPromptId } : {}),
            ...(verboseLogs ? { verbose: true } : {}),
          }),
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to send message');
        setText('');
        setAttachments([]);
        // Try to kick off tracking immediately using the newly created request id
        try {
          const reqId = json?.data?.request?.request_id || json?.data?.request_id;
          if (reqId) {
            // Targeted refresh ensures the request shows up in Active Requests right away
            await refreshActiveRequests([reqId]);
          }
        } catch (_) { /* no-op; fall back to regular refresh below */ }
        // Reload first-page seed so hidden request marker (inserted by backend) appears in history
        await loadMessages(1);
        // Fallback: if we didn't get a request id, perform a generic refresh
        if (!(json?.data?.request?.request_id || json?.data?.request_id)) {
          refreshActiveRequests();
        }
      }
    } catch (e) {
      setSendError(e.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const onScroll = async (e) => {
    const el = e.currentTarget;
    // Infinite scroll: when the user reaches near the top, load older messages via window API
    const TOP_THRESHOLD = 50; // px
    if (el.scrollTop <= TOP_THRESHOLD && initialScrollDoneRef.current && !reachedStart && !loadingMsgs && !loadingOlder) {
      await loadOlderWindow();
    }
    // Update sticky flag based on proximity to bottom
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const halfView = el.clientHeight / 2;
    stickyRef.current = distanceFromBottom <= halfView;
  };

  // Auto-scroll to bottom when a new message arrives and we're within threshold of the bottom
  useEffect(() => {
    if (!listRef.current) return undefined;
    if (!stickyRef.current) return undefined;
    const id = requestAnimationFrame(() => {
      if (bottomRef.current && typeof bottomRef.current.scrollIntoView === 'function') {
        try { bottomRef.current.scrollIntoView({ block: 'end' }); } catch (_) { /* no-op */ }
      }
      // Fallback manual scroll
      listRef.current.scrollTop = listRef.current.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [messages.length]);

  // Apply sticky auto-scroll when the chat-messages container height changes (e.g., window resize)
  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;

    const maybeStickToBottom = () => {
      // Update sticky state based on current distance from bottom (within half view height)
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const halfView = el.clientHeight / 2;
      const isNearBottom = distanceFromBottom <= halfView;
      stickyRef.current = isNearBottom;
      if (!stickyRef.current) return;
      const id = requestAnimationFrame(() => {
        if (bottomRef.current && typeof bottomRef.current.scrollIntoView === 'function') {
          try { bottomRef.current.scrollIntoView({ block: 'end' }); } catch (_) { /* no-op */ }
        }
        // Fallback manual scroll
        el.scrollTop = el.scrollHeight;
      });
      // Clean up RAF if another resize fires quickly
      return () => cancelAnimationFrame(id);
    };

    let cleanupRaf;

    // Observe size changes of the container
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const c = maybeStickToBottom();
          if (cleanupRaf) cleanupRaf();
          cleanupRaf = c;
        });
      });
      try { ro.observe(el); } catch (_) { /* no-op */ }
    }

    // Fallback: also react on window resize
    const onWinResize = () => {
      const c = maybeStickToBottom();
      if (cleanupRaf) cleanupRaf();
      cleanupRaf = c;
    };
    window.addEventListener('resize', onWinResize);

    return () => {
      if (ro) {
        try { ro.disconnect(); } catch (_) { /* no-op */ }
      }
      window.removeEventListener('resize', onWinResize);
      if (cleanupRaf) cleanupRaf();
    };
  }, []);

  // Build display list including request markers
  const replyRequestByMessageId = useMemo(() => {
    const map = new Map();
    for (const req of activeRequests || []) {
      const ids = Array.isArray(req.response_messages) ? req.response_messages : [];
      if (ids.length > 0) {
        map.set(ids[ids.length - 1], req);
      }
    }
    return map;
  }, [activeRequests]);

  const showReplyCopyNotice = useCallback((messageId, label) => {
    if (replyCopyNoticeTimerRef.current) clearTimeout(replyCopyNoticeTimerRef.current);
    setReplyCopyNotice({ messageId, text: label });
    replyCopyNoticeTimerRef.current = setTimeout(() => {
      setReplyCopyNotice({ messageId: null, text: '' });
      replyCopyNoticeTimerRef.current = null;
    }, 2000);
  }, []);

  useEffect(() => () => {
    if (replyCopyNoticeTimerRef.current) clearTimeout(replyCopyNoticeTimerRef.current);
  }, []);

  const handleCopyAgentReply = useCallback(async (message, request) => {
    try {
      let text = '';
      if (request) {
        text = collectRequestReplyText(request, messages);
        if (!text && Array.isArray(request.response_messages) && request.response_messages.length > 0) {
          const loaded = await fetchMessagesByIds(API_BASE_URL, request.response_messages, getAuthHeaders);
          text = collectRequestReplyText({ ...request, response_messages: request.response_messages }, loaded);
        }
      } else {
        text = extractMessageContent(message).trim();
      }
      if (!text) {
        showReplyCopyNotice(message.message_id, 'No reply yet');
        return;
      }
      const ok = await copyTextToClipboard(text);
      showReplyCopyNotice(message.message_id, ok ? 'Reply copied' : 'Copy failed');
    } catch (err) {
      showReplyCopyNotice(message.message_id, err?.message || 'Copy failed');
    }
  }, [messages, showReplyCopyNotice]);

  const displayItems = useMemo(() => {
    const items = [];
    (messages || []).forEach(m => {
      if (m?.metadata && m.metadata.visible === false) {
        try {
          const parsed = JSON.parse(m.content);
          if (parsed?.request_id) {
            const req = (activeRequests || []).find(r => r.request_id === parsed.request_id) || { request_id: parsed.request_id, status: 'queued', created_at: m.created_at };
            items.push({ type: 'request', id: `req-${parsed.request_id}`, created_at: m.created_at, request: req });
            return;
          }
        } catch (_) {}
      }
      items.push({ type: 'message', id: m.message_id, created_at: m.created_at, msg: m });
    });
    // Sort strictly by time, and ensure the request block (thinking) appears AFTER the just-sent message when timestamps tie
    items.sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      if (ta !== tb) return ta - tb;
      // Prefer normal messages before request blocks on identical timestamps
      if (a.type !== b.type) return a.type === 'message' ? -1 : 1;
      // Stable fallback by id string
      const ai = String(a.id || '');
      const bi = String(b.id || '');
      if (ai < bi) return -1;
      if (ai > bi) return 1;
      return 0;
    });
    return items;
  }, [messages, activeRequests]);

  // Manual top-load handler (for when the list is too short to scroll)
  const handleLoadOlder = async () => {
    if (loadingMsgs || loadingOlder) return;
    await loadOlderWindow();
  };

  // If we navigate to a page > 1 due to totalPages change, ensure the reachedStart flag is cleared
  useEffect(() => {
    if (page > 1 && reachedStart) {
      setReachedStart(false);
    }
  }, [page, reachedStart]);

  return (
    <div className="chat-page">
      <div className="chat-messages" ref={listRef} onScroll={onScroll}>
        {/* Top controls: load older or beginning indicator */}
        <div className="chat-top-controls">
          {messages.length > 0 && !reachedStart ? (
            <button className="load-older-btn" onClick={handleLoadOlder} disabled={loadingMsgs || loadingOlder}>
              {loadingOlder ? (<><i className="fas fa-spinner fa-pulse" /> Loading older…</>) : (<><i className="fas fa-arrow-up" /> Load older messages</>)}
            </button>
          ) : (
            <div className="history-start-ind">
              <i className="fas fa-flag-checkered" /> Beginning of chat history
            </div>
          )}
        </div>
        <div className="chat-header">
          {loadingSession ? (
            <span className="chat-header-meta"><i className="fas fa-spinner fa-pulse" /> Loading…</span>
          ) : errorSession ? (
            <span className="chat-header-meta"><i className="fas fa-triangle-exclamation" /> {errorSession}</span>
          ) : (
            <div className="chat-header-row">
              {session?.owner?.email && (
                <span className="chat-session-owner" title={`Created by ${session.owner.email}`}>
                  <i className="fas fa-user" /> {session.owner.email.split('@')[0]}
                </span>
              )}
              {projectId && sessionId && (
                <NavLink
                  className="chat-summary-link"
                  to={`/p/${projectId}/summary?session=${encodeURIComponent(sessionId)}`}
                >
                  <i className="fas fa-clipboard-list" /> Project summary
                </NavLink>
              )}
              {projectId && sessionId && (
                <button
                  type="button"
                  className="chat-summary-link"
                  style={{ border: 'none', background: 'transparent', cursor: updatingSummary ? 'wait' : 'pointer', font: 'inherit', color: 'inherit' }}
                  disabled={updatingSummary || archivingChat}
                  title="Compress this chat into your project summary (manual)"
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Update project summary from this chat?\n\n' +
                          'The assistant will compress this conversation into your saved project summary ' +
                          '(merging with any existing summary). You can review and edit it on the next screen.\n\n' +
                          'This does not run automatically — only when you click this button.'
                      )
                    ) {
                      return;
                    }
                    setUpdatingSummary(true);
                    try {
                      await updateProjectSummaryFromSession(projectId, sessionId);
                      navigate(`/p/${projectId}/summary?session=${encodeURIComponent(sessionId)}`, {
                        state: { summaryUpdatedFromChat: true },
                      });
                    } catch (e) {
                      window.alert(e.message || 'Failed to update project summary');
                    } finally {
                      setUpdatingSummary(false);
                    }
                  }}
                >
                  {updatingSummary ? (
                    <><i className="fas fa-spinner fa-pulse" /> Updating summary…</>
                  ) : (
                    <><i className="fas fa-compress" /> Update summary</>
                  )}
                </button>
              )}
              {projectId && sessionId && (
                <button
                  type="button"
                  className="chat-summary-link"
                  style={{ border: 'none', background: 'transparent', cursor: archivingChat ? 'wait' : 'pointer', font: 'inherit', color: 'inherit' }}
                  disabled={archivingChat || updatingSummary}
                  title="Archive this chat"
                  onClick={async () => {
                    if (!window.confirm('Archive this chat? You can show archived chats from the sidebar to open it again.')) return;
                    setArchivingChat(true);
                    try {
                      const headers = await getAuthHeaders();
                      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/archive`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({ sessionId, archived: true }),
                      });
                      const json = await resp.json().catch(() => ({}));
                      if (!resp.ok || !json?.success) {
                        throw new Error(json?.message || json?.error || 'Could not archive chat');
                      }
                      navigate(`/p/${projectId}`);
                    } catch (e) {
                      window.alert(e.message || 'Archive failed');
                    } finally {
                      setArchivingChat(false);
                    }
                  }}
                >
                  {archivingChat ? (
                    <><i className="fas fa-spinner fa-pulse" /> Archiving…</>
                  ) : (
                    <><i className="fas fa-box-archive" /> Archive chat</>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
        {loadingMsgs && messages.length === 0 && (
          <div style={{ padding: 16, color: '#6b7280' }}><i className="fas fa-spinner fa-pulse" /> Loading messages…</div>
        )}
        {errorMsgs && (
          <div style={{ padding: 16, color: '#b91c1c' }}><i className="fas fa-triangle-exclamation" /> {errorMsgs}</div>
        )}
        {displayItems.map(it => {
          if (it.type === 'message') {
            const m = it.msg;
            // Use helper to extract content (prefers model_message metadata when present)
            const content = extractMessageContent(m);
            return (
              <div key={it.id} className={`chat-message ${m.is_agent ? 'agent' : 'user'}`}>
                <ChatMessageContent
                  content={content}
                  markdown={!!m.is_agent && chatMarkdownEnabled !== false}
                />
                {m.is_agent && Array.isArray(m?.metadata?.web_search_sources) && m.metadata.web_search_sources.length > 0 && (
                  <WebSearchSourcesList sources={m.metadata.web_search_sources} />
                )}
                {Array.isArray(m?.metadata?.yuml_emissions) && m.metadata.yuml_emissions.length > 0 && (
                  <YumlEmissionLink emissions={m.metadata.yuml_emissions} />
                )}
                {Array.isArray(m?.metadata?.files) && m.metadata.files.length > 0 && (
                  <div className="message-files">
                    {m.metadata.files.map((f, idx) => {
                      const size = f?.size_bytes ?? f?.size;
                      const name = f?.name || 'Unnamed file';
                      return (
                        <div key={f?.id || idx} className="message-file" title={`${name}${size ? ` • ${size} bytes` : ''}`}>
                          <i className="fas fa-paperclip" />
                          <span className="file-name">{name}</span>
                          {size != null && <span className="file-size">({formatBytes(size)})</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {m.is_agent && content && (
                  <div className="message-actions">
                    <button
                      type="button"
                      className="message-copy-btn"
                      title="Copy reply"
                      aria-label="Copy reply"
                      onClick={() => handleCopyAgentReply(m, replyRequestByMessageId.get(m.message_id))}
                    >
                      <i className="fas fa-copy" />
                    </button>
                    <button
                      type="button"
                      className="btn ghost"
                      style={{ fontSize: 12, padding: '4px 10px' }}
                      onClick={() => setIngestMessage(m)}
                    >
                      <i className="fas fa-database" /> Ingest
                    </button>
                    {lockedRecord ? (
                      <button
                        type="button"
                        className="btn ghost"
                        style={{ fontSize: 12, padding: '4px 10px' }}
                        onClick={() => setUnlockConfirmOpen(true)}
                        disabled={lockBusy}
                      >
                        <i className="fas fa-lock-open" /> Unlock Record
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn ghost"
                        style={{ fontSize: 12, padding: '4px 10px' }}
                        onClick={() => {
                          setFindRecordGroupSlug(null);
                          setFindRecordOpen(true);
                        }}
                      >
                        <i className="fas fa-search" /> Find Record
                      </button>
                    )}
                    {replyCopyNotice.messageId === m.message_id && replyCopyNotice.text && (
                      <span className="message-copy-notice">{replyCopyNotice.text}</span>
                    )}
                  </div>
                )}
                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>{new Date(m.created_at).toLocaleString()}</div>
              </div>
            );
          }
          const r = it.request;
          return (
            <RequestHistoryBlock
              key={it.id}
              request={r}
              expandedReqs={expandedReqs}
              toggleRequestHistory={toggleRequestHistory}
              ensureRequestHistory={ensureRequestHistory}
              renderMarkdown={chatMarkdownEnabled !== false}
              showVerboseLogs={verboseLogs}
            />
          );
        })}
        {!loadingMsgs && !errorMsgs && messages.length === 0 && (
          <div style={{ padding: 16, color: '#6b7280' }}>No messages yet</div>
        )}
        {/* Paging controls removed: chat uses infinite scroll to load older messages when scrolling to the top */}
        {/* Bottom sentinel to aid reliable initial scroll-to-bottom */}
        <div ref={bottomRef} />
      </div>
      <div
        className={`chat-composer${dragOver ? ' drag-over' : ''}`}
        onDragOver={(e) => {
          if (!canAttachAnyFile) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={async (e) => {
          e.preventDefault();
          setDragOver(false);
          if (!canAttachAnyFile) return;
          if (e.dataTransfer?.files?.length) await addFiles(e.dataTransfer.files);
        }}
      >
        {/* Hidden file input for manual selection */}
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} disabled={!canAttachAnyFile} onChange={async (e) => { await addFiles(e.target.files); e.target.value = ''; }} />
        {attachments?.length > 0 && (
          <div className="attachments-list">
            {attachments.map((f, idx) => (
              <div key={idx} className="attachment-chip" title={`${f.name} (${f.size} bytes)`}>
                <i className="fas fa-paperclip" /> <span className="name">{f.name}</span>
                <button type="button" className="remove" aria-label="Remove" onClick={() => removeAttachmentAt(idx)}>
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}
        {lockedRecord && (
          <div className="attachments-list" style={{ marginBottom: 6 }}>
            <div
              className="attachment-chip"
              title={`${lockedRecord.group_slug} · ${lockedRecord.entry_id}`}
              style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}
            >
              <i className="fas fa-lock" />
              <span className="name">
                Locked: {lockedRecord.label || lockedRecord.entry_id}
              </span>
              <button
                type="button"
                className="remove"
                aria-label="Unlock record"
                title="Unlock record"
                onClick={() => setUnlockConfirmOpen(true)}
                disabled={lockBusy}
              >
                <i className="fas fa-lock-open" />
              </button>
            </div>
          </div>
        )}
        <MessageInputEditor
          value={text}
          onChange={setText}
          onSend={handleSend}
          disabled={sending || anyPending}
          autoExpandTags={true}
          providers={{ url: UrlTagProvider }}
          footerButtonsLeft={(
            <>
              <button type="button" className="mi-btn" title="Attach files" onClick={() => fileInputRef.current?.click()} disabled={!canAttachAnyFile}>
                <i className="fas fa-plus" /> Attach
              </button>
              {!canAttachAnyFile && (
                <span className="mi-hint">Current model does not accept file attachments.</span>
              )}
              {attachments?.length > 0 && (
                <span className="mi-hint">{attachments.length} file{attachments.length !== 1 ? 's' : ''} attached</span>
              )}
            </>
          )}
          footerButtonsRight={(
            <>
              {isSuperAdmin && (
                <label className="chat-verbose-toggle" title="Show raw request-history metadata and log model invoke/response payloads for this session">
                  <input
                    type="checkbox"
                    checked={verboseLogs}
                    onChange={(e) => setVerboseLogs(e.target.checked)}
                    disabled={sending}
                  />
                  Verbose logs
                </label>
              )}
              {sending ? (<span className="mi-hint"><i className="fas fa-spinner fa-pulse" /> Sending…</span>) : anyPending ? (<span className="mi-hint"><i className="fas fa-hourglass-half" /> Processing…</span>) : null}
            </>
          )}
          belowFooter={(
            <ProjectWorkflowButtonsCarousel
              projectId={projectId}
              sessionId={sessionId}
              disabled={sending || anyPending}
              composerText={text}
              messages={messages}
              onSetComposerText={(msg) => { setText(msg); setSendError(''); }}
              onRunWorkflow={({ content, workflowPromptId }) => handleSend(content, { workflowPromptId })}
              projectToolIds={projectToolIds}
              projectGroupSlugs={projectGroupSlugs}
            />
          )}
        />
      </div>
      {sendError && <div style={{ color: '#b91c1c', padding: '4px 16px 12px 16px' }}><i className="fas fa-triangle-exclamation" /> {sendError}</div>}

      <ConversationIngestModal
        open={!!ingestMessage}
        suffix={activeSuffix}
        projectId={projectId}
        sessionId={sessionId}
        message={ingestMessage}
        onClose={() => setIngestMessage(null)}
        onOpenFindRecord={({ groupSlug } = {}) => {
          setFindRecordGroupSlug(groupSlug || null);
          setFindRecordOpen(true);
        }}
      />

      <FindRecordModal
        open={findRecordOpen}
        suffix={activeSuffix}
        projectId={projectId}
        sessionId={sessionId}
        initialGroupSlug={findRecordGroupSlug}
        onLocked={(record) => {
          setLockedRecord(record || null);
          setFindRecordOpen(false);
        }}
        onClose={() => setFindRecordOpen(false)}
      />

      <EmbeddingWaitModal
        open={embeddingWaitOpen}
        sessionId={sessionId}
        waitRequest={embeddingWaitRequest}
        defaultDeferredMessage={text.trim() || lastUserMessageText}
        onClose={() => setEmbeddingWaitOpen(false)}
        onWaitComplete={() => {
          const retry =
            lastUserMessageText
            || 'Embeddings finished. Please retry the previous search and answer my last question.';
          handleSend(retry);
        }}
        onQueueStarted={({ request_id: requestId, deferred_message: deferred }) => {
          setEmbeddingWaitRequest((prev) => ({
            ...(prev || {}),
            mode: 'queue',
            request_id: requestId,
            deferred_message: deferred,
          }));
        }}
      />

      <ToolChainWaitModal
        open={toolChainWaitOpen}
        sessionId={sessionId}
        waitRequest={toolChainWaitRequest}
        onClose={() => setToolChainWaitOpen(false)}
        onResumed={(data) => {
          setToolChainWaitRequest(null);
          if (data?.awaiting_user) {
            setToolChainWaitRequest(data);
            setToolChainWaitOpen(true);
            refreshLockedRecord({ openToolChainWaitIfRequested: true });
          } else {
            // Soft refresh so the user sees the continue notice / can ask the model to proceed
            refreshLockedRecord();
          }
        }}
      />

      <EntryUpdateWaitModal
        open={entryUpdateWaitOpen}
        sessionId={sessionId}
        waitRequest={entryUpdateWaitRequest}
        onClose={() => {
          setEntryUpdateWaitOpen(false);
          setEntryUpdateWaitRequest(null);
          refreshLockedRecord();
        }}
        onConfirmed={() => {
          setEntryUpdateWaitRequest(null);
          refreshLockedRecord();
        }}
      />

      {unlockConfirmOpen && (
        <div className="conversation-ingest-overlay" role="dialog" aria-modal="true">
          <div className="conversation-ingest-modal" style={{ width: 'min(480px, 94vw)', minHeight: 'auto' }}>
            <div className="conversation-ingest-header">
              <h3>Unlock Record</h3>
            </div>
            <div className="conversation-ingest-body">
              <p>
                Release <strong>{lockedRecord?.label || lockedRecord?.entry_id || 'this record'}</strong> from the conversation?
              </p>
              <p className="conversation-ingest-hint">
                Future prompts will no longer be focused on this record.
              </p>
            </div>
            <div className="conversation-ingest-footer">
              <button type="button" className="btn ghost" onClick={() => setUnlockConfirmOpen(false)} disabled={lockBusy}>
                No
              </button>
              <button type="button" className="btn" onClick={handleUnlockRecord} disabled={lockBusy}>
                Yes, unlock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionChatPage;

function formatVerboseMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return null;
  try {
    return JSON.stringify(metadata, null, 2);
  } catch (_) {
    return String(metadata);
  }
}

// Local component for rendering the request's Thinking History block
function RequestHistoryBlock({
  request,
  expandedReqs,
  toggleRequestHistory,
  ensureRequestHistory,
  renderMarkdown = true,
  showVerboseLogs = false,
}) {
  const reqId = request.request_id;
  const state = expandedReqs[reqId] || {};
  const open = !!state.open;
  const loading = !!state.loading;
  const error = state.error || null;
  const [copyNotice, setCopyNotice] = useState('');
  const copyNoticeTimerRef = useRef(null);
  // Memoize items to keep a stable reference for effects and avoid re-creating on each render
  const items = React.useMemo(() => {
    const arr = Array.isArray(state.items) ? [...state.items] : [];
    // sort oldest -> newest
    arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return arr;
  }, [state.items]);
  const listRef = useRef(null);
  const rootRef = useRef(null);
  // Closed-view footer animation state
  const [footerCurr, setFooterCurr] = useState(null);
  const [footerPrev, setFooterPrev] = useState(null);
  const [footerPhase, setFooterPhase] = useState('idle'); // idle | out | in
  const animTimerRef = useRef(null);

  const showCopyNotice = useCallback((label) => {
    if (copyNoticeTimerRef.current) clearTimeout(copyNoticeTimerRef.current);
    setCopyNotice(label);
    copyNoticeTimerRef.current = setTimeout(() => {
      setCopyNotice('');
      copyNoticeTimerRef.current = null;
    }, 2000);
  }, []);

  useEffect(() => () => {
    if (copyNoticeTimerRef.current) clearTimeout(copyNoticeTimerRef.current);
  }, []);

  const loadHistoryItems = useCallback(async () => {
    if (Array.isArray(state.items) && state.items.length > 0) {
      return state.items;
    }
    const headers = await getAuthHeaders();
    const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/message/request/history`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ requestId: reqId, page: 1, limit: 500 }),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || !json?.success) {
      throw new Error(json?.message || json?.error || 'Failed to load thinking history');
    }
    return json.data?.items || [];
  }, [reqId, state.items]);

  const handleCopyThinking = useCallback(async (e) => {
    e.stopPropagation();
    try {
      const historyItems = await loadHistoryItems();
      const text = formatThinkingHistoryAudit(historyItems, {
        includeVerboseMetadata: showVerboseLogs,
        request,
      });
      if (!text.trim()) {
        showCopyNotice('No thinking history yet');
        return;
      }
      const ok = await copyTextToClipboard(text);
      showCopyNotice(ok ? 'Thinking copied' : 'Copy failed');
    } catch (err) {
      showCopyNotice(err?.message || 'Copy failed');
    }
  }, [loadHistoryItems, showVerboseLogs, showCopyNotice, request]);

  // auto-scroll to bottom when opened or when new items arrive
  useEffect(() => {
    if (open && listRef.current) {
      // wait for paint after CSS open transition
      const id = requestAnimationFrame(() => {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open, items]);

  // Prefetch history when the block becomes visible in the viewport
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          ensureRequestHistory(reqId);
        }
      });
    }, { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [reqId, ensureRequestHistory]);

  // Send footer update events with last history item
  useEffect(() => {
    const lastItem = items.length > 0 ? items[items.length - 1] : null;
    try {
      // When panel is open, hide footer; when closed and we have content, show with last item
      if (open) {
        window.dispatchEvent(new CustomEvent('cascade:req-history-footer', { detail: { requestId: reqId, visible: false } }));
      } else if (lastItem) {
        window.dispatchEvent(new CustomEvent('cascade:req-history-footer', { detail: { requestId: reqId, visible: true, item: lastItem } }));
      }
    } catch (_) { /* no-op */ }
  }, [open, items, reqId]);

  // Manage closed footer content + animations on last-item change
  useEffect(() => {
    const lastItem = items.length > 0 ? items[items.length - 1] : null;
    // Clear timers on unmount or re-run
    if (animTimerRef.current) {
      clearTimeout(animTimerRef.current);
      animTimerRef.current = null;
    }
    // First-time assignment (no animation)
    if (!footerCurr && lastItem) {
      setFooterCurr(lastItem);
      setFooterPrev(null);
      setFooterPhase('idle');
      return;
    }
    // If changed, animate out old then in new
    const changed = (footerCurr?.thought_id || null) !== (lastItem?.thought_id || null);
    if (changed) {
      // Start leaving phase for current (if any)
      if (footerCurr) {
        setFooterPrev(footerCurr);
        setFooterPhase('out');
      }
      // After short delay, swap to new and animate in
      animTimerRef.current = setTimeout(() => {
        setFooterCurr(lastItem || null);
        setFooterPhase(lastItem ? 'in' : 'idle');
        // Clear prev after enter animation completes
        animTimerRef.current = setTimeout(() => {
          setFooterPrev(null);
          setFooterPhase('idle');
        }, 220);
      }, 160);
    }
    return () => {
      if (animTimerRef.current) {
        clearTimeout(animTimerRef.current);
        animTimerRef.current = null;
      }
    };
  }, [items, footerCurr]);

  return (
    <div ref={rootRef} className={`chat-message request-block`}>
      <div className="req-header" role="button" tabIndex={0} onClick={() => toggleRequestHistory(reqId)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRequestHistory(reqId); } }}>
        <div className="req-title">
          <strong>Thinking History</strong>
          <div className="req-meta">
            Status: <span style={{ textTransform: 'capitalize' }}>{request.status}</span>
            {' • Time: '}
            {isActiveChatRequest(request) ? (
              <DurationTimer start={request.created_at} />
            ) : (
              formatDuration(request.created_at, request.modified_at)
            )}
            {copyNotice ? <span className="req-copy-notice"> • {copyNotice}</span> : null}
          </div>
        </div>
        <div className="req-header-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="req-copy-btn req-copy-btn-icon"
            title="Copy thinking"
            aria-label="Copy thinking"
            onClick={handleCopyThinking}
          >
            <i className="fas fa-copy" />
          </button>
          <div className="chevron-btn" aria-label={open ? 'Hide thinking history' : 'Show thinking history'}>
            <i className={`fas fa-chevron-right ${open ? 'rotated' : ''}`}></i>
          </div>
        </div>
      </div>

      <div className={`history-outer ${open ? 'open' : ''}`}>
        {open && (
          <div ref={listRef} className="history-inner">
            {loading && (<div style={{ color: '#6b7280' }}><i className="fas fa-spinner fa-pulse" /> Loading…</div>)}
            {error && (<div style={{ color: '#b91c1c' }}><i className="fas fa-triangle-exclamation" /> {error}</div>)}
            {!loading && !error && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {items.map(th => (
                  <li key={th.thought_id} style={{ marginBottom: 6 }}>
                    <ChatMessageContent content={th.reasoning} markdown={renderMarkdown} />
                    {showVerboseLogs && th.metadata && Object.keys(th.metadata).length > 0 && (
                      <pre className="history-verbose-meta">{formatVerboseMetadata(th.metadata)}</pre>
                    )}
                    {(() => {
                      const embeddingStatus = formatEmbeddingStatusMeta(th.metadata);
                      if (!embeddingStatus) return null;
                      return (
                        <div style={{ marginTop: 3, fontSize: '0.85em', color: embeddingStatus.warning ? '#f59e0b' : '#6b7280' }}>
                          <i className={`fas ${embeddingStatus.warning ? 'fa-triangle-exclamation' : 'fa-clock'}`} style={{ marginRight: 4 }}></i>
                          {embeddingStatus.text}
                          {embeddingStatus.warning ? ` — ${embeddingStatus.warning}` : ''}
                        </div>
                      );
                    })()}
                  </li>
                ))}
                {items.length === 0 && (
                  <li style={{ color: '#6b7280' }}>No history</li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
      {/* Closed view: show last history item pinned to bottom of block */}
      {!open && (footerCurr || footerPrev) && (
        <div className="history-footer" aria-live="polite" aria-atomic="true">
          {footerPrev && (
            <div key={`prev-${footerPrev.thought_id}`} className={`hf-item leaving`}>
              <span className="hf-label">Last:</span>
              <span className="hf-text">{footerPrev.reasoning}</span>
            </div>
          )}
          {footerCurr && (
            <div key={`curr-${footerCurr.thought_id}`} className={`hf-item ${footerPhase === 'in' ? 'entering' : ''}`}>
              <span className="hf-label">Last:</span>
              <span className="hf-text">{footerCurr.reasoning}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
